import { GlAlert, GlBadge, GlKeysetPagination, GlPagination } from '@gitlab/ui';
import EmptyResult from '~/vue_shared/components/empty_result.vue';
import LocalStorageSync from '~/vue_shared/components/local_storage_sync.vue';
import PageSizeSelector from '~/vue_shared/components/page_size_selector.vue';
import { updateHistory, setUrlParams } from '~/lib/utils/url_utility';
import { __ } from '~/locale';
import { DRAG_DELAY } from '~/sortable/constants';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import FilteredSearchBar from '~/vue_shared/components/filtered_search_bar/filtered_search_bar_root.vue';
import ResourceListsLoadingStateList from '~/vue_shared/components/resource_lists/loading_state_list.vue';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
import { DEFAULT_SKELETON_COUNT, PAGE_SIZE_STORAGE_KEY } from '../constants';
import IssuableBulkEditSidebar from './issuable_bulk_edit_sidebar.vue';
import IssuableItem from './issuable_item.vue';
import IssuableTabs from './issuable_tabs.vue';
const VueDraggable = ()=>import('~/lib/utils/vue3compat/draggable_compat.vue');
export default {
    name: 'IssuableListRoot',
    vueDraggableAttributes: {
        animation: 200,
        forceFallback: false,
        ghostClass: 'gl-invisible',
        tag: 'ul',
        delay: DRAG_DELAY,
        delayOnTouchOnly: true
    },
    components: {
        GlAlert,
        GlBadge,
        GlKeysetPagination,
        IssuableTabs,
        FilteredSearchBar,
        IssuableItem,
        IssuableBulkEditSidebar,
        GlPagination,
        VueDraggable,
        PageSizeSelector,
        ResourceListsLoadingStateList,
        LocalStorageSync,
        EmptyResult
    },
    mixins: [
        glSlotsMixin
    ],
    props: {
        namespace: {
            type: String,
            required: true
        },
        fullPath: {
            type: String,
            required: false,
            default: null
        },
        recentSearchesStorageKey: {
            type: String,
            required: true
        },
        searchInputPlaceholder: {
            type: String,
            required: false,
            default: __('Search or filter results…')
        },
        searchTokens: {
            type: Array,
            required: true
        },
        sortOptions: {
            type: Array,
            required: true
        },
        urlParams: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        initialFilterValue: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        initialSortBy: {
            type: String,
            required: false,
            default: 'created_desc'
        },
        issuables: {
            type: Array,
            required: true
        },
        tabs: {
            type: Array,
            required: true
        },
        tabCounts: {
            type: Object,
            required: false,
            default: null
        },
        truncateCounts: {
            type: Boolean,
            required: false,
            default: false
        },
        currentTab: {
            type: String,
            required: true
        },
        issuableSymbol: {
            type: String,
            required: false,
            default: '#'
        },
        issuablesLoading: {
            type: Boolean,
            required: false,
            default: false
        },
        showPaginationControls: {
            type: Boolean,
            required: false,
            default: false
        },
        showBulkEditSidebar: {
            type: Boolean,
            required: false,
            default: false
        },
        defaultPageSize: {
            type: Number,
            required: false,
            default: 20
        },
        totalItems: {
            type: Number,
            required: false,
            default: 0
        },
        currentPage: {
            type: Number,
            required: false,
            default: 1
        },
        previousPage: {
            type: Number,
            required: false,
            default: 0
        },
        nextPage: {
            type: Number,
            required: false,
            default: 2
        },
        hasScopedLabelsFeature: {
            type: Boolean,
            required: false,
            default: false
        },
        labelFilterParam: {
            type: String,
            required: false,
            default: undefined
        },
        isManualOrdering: {
            type: Boolean,
            required: false,
            default: false
        },
        useKeysetPagination: {
            type: Boolean,
            required: false,
            default: false
        },
        hasNextPage: {
            type: Boolean,
            required: false,
            default: false
        },
        hasPreviousPage: {
            type: Boolean,
            required: false,
            default: false
        },
        error: {
            type: String,
            required: false,
            default: ''
        },
        syncFilterAndSort: {
            type: Boolean,
            required: false,
            default: false
        },
        showFilteredSearchFriendlyText: {
            type: Boolean,
            required: false,
            default: false
        },
        showPageSizeSelector: {
            type: Boolean,
            required: false,
            default: false
        },
        showWorkItemTypeIcon: {
            type: Boolean,
            required: false,
            default: false
        },
        activeIssuable: {
            type: Object,
            required: false,
            default: null
        },
        preventRedirect: {
            type: Boolean,
            required: false,
            default: false
        },
        issuableItemClass: {
            type: String,
            required: false,
            default: ''
        },
        detailLoading: {
            type: Boolean,
            required: false,
            default: false
        },
        hiddenMetadataKeys: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        alwaysAllowCustomEmptyState: {
            type: Boolean,
            required: false,
            default: false
        },
        searchTimeout: {
            type: Boolean,
            required: false,
            default: false
        },
        maxCount: {
            type: Number,
            required: false,
            default: null
        }
    },
    emits: [
        'click-tab',
        'dismiss-alert',
        'filter',
        'next-page',
        'page-change',
        'page-size-change',
        'previous-page',
        'reorder',
        'select-issuable',
        'sort',
        'update-legacy-bulk-edit'
    ],
    data () {
        return {
            checkedIssuableIds: []
        };
    },
    computed: {
        skeletonItemCount () {
            const { totalItems, defaultPageSize, currentPage } = this;
            const totalPages = Math.ceil(totalItems / defaultPageSize);
            if (totalPages) {
                return currentPage < totalPages ? defaultPageSize : totalItems % defaultPageSize || defaultPageSize;
            }
            return DEFAULT_SKELETON_COUNT;
        },
        allIssuablesChecked () {
            return this.checkedIssuables.length === this.issuables.length;
        },
        checkedIssuables () {
            return this.issuables.filter((issuable)=>this.checkedIssuableIds.includes(issuable.id));
        },
        issuablesWrapper () {
            return this.isManualOrdering ? VueDraggable : 'ul';
        },
        hasItems () {
            return this.issuables.length > 0;
        }
    },
    watch: {
        urlParams: {
            deep: true,
            immediate: true,
            handler (params) {
                if (Object.keys(params).length) {
                    updateHistory({
                        url: setUrlParams(params, {
                            url: window.location.href,
                            clearParams: true,
                            decodeParams: true
                        }),
                        title: document.title,
                        replace: true
                    });
                }
            }
        },
        showBulkEditSidebar () {
            this.checkedIssuableIds = [];
        }
    },
    methods: {
        isIssuableChecked (issuable) {
            return this.checkedIssuableIds.includes(issuable.id);
        },
        updateCheckedIssuableIds (issuable, toCheck) {
            const isIdChecked = this.checkedIssuableIds.includes(issuable.id);
            if (toCheck && !isIdChecked) {
                this.checkedIssuableIds.push(issuable.id);
            }
            if (!toCheck && isIdChecked) {
                const indexToDelete = this.checkedIssuableIds.findIndex((id)=>id === issuable.id);
                this.checkedIssuableIds.splice(indexToDelete, 1);
            }
        },
        handleIssuableCheckedInput (issuable, value) {
            this.updateCheckedIssuableIds(issuable, value);
            this.$emit('update-legacy-bulk-edit');
        },
        handleAllIssuablesCheckedInput (value) {
            this.issuables.forEach((issuable)=>this.updateCheckedIssuableIds(issuable, value));
            this.$emit('update-legacy-bulk-edit');
        },
        handleVueDraggableUpdate ({ newIndex, oldIndex }) {
            this.$emit('reorder', {
                newIndex,
                oldIndex
            });
        },
        handlePageSizeChange (newPageSize) {
            this.$emit('page-size-change', newPageSize);
        },
        isIssuableActive (issuable) {
            return Boolean(getIdFromGraphQLId(issuable.id) === getIdFromGraphQLId(this.activeIssuable?.id));
        }
    },
    PAGE_SIZE_STORAGE_KEY
};
